import { Authenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";

import { useMemo, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";

import CustomerGuard from "./components/CustomerGuard";
import CustomerLayout from "./CustomerLayout";
import CustomerDashboard from "./pages/CustomerDashboard";
import CustomerOrgDetails from "./pages/CustomerOrgDetails";
import CustomerUsers from "./pages/CustomerUsers";
import CustomerOrgNotifications from "./pages/CustomerOrgNotifications";
import CustomerUserProfile from "./pages/CustomerUserProfile";
import CustomerSoftwareDownloads from "./pages/CustomerSoftwareDownloads";
import Account from "./pages/Account";
import Notifications from "./pages/Notifications";
import CustomerSlideLookup from "./pages/CustomerSlideLookup";
import CustomerSlideDetail from "./pages/CustomerSlideDetail";
import CustomerSlideUpload from "./pages/CustomerSlideUpload";
import CustomerUploadActivity from "./pages/CustomerUploadActivity";
import CustomerReports from "./pages/CustomerReports";
import ImpersonateBootstrap from "./pages/ImpersonateBootstrap";
import RequireCustomerRole from "./components/RequireCustomerRole";
import {
  ROLE_BILLING,
  ROLE_IT,
  ROLE_OPERATIONS,
  ROLE_VIEWER,
} from "./customerRoles";
import {
  clearImpersonationSession,
  hasImpersonationSession,
} from "./impersonationSession";

function RedirectLegacyProfileUser() {
  const { username } = useParams();
  return (
    <Navigate
      to={`/organization/users/${encodeURIComponent(username || "")}`}
      replace
    />
  );
}

function PortalRoutes({ user, signOut }) {
  return (
    <Routes>
      <Route path="impersonate" element={<ImpersonateBootstrap />} />
      <Route path="/" element={<CustomerGuard user={user} signOut={signOut} />}>
        <Route element={<CustomerLayout user={user} signOut={signOut} />}>
          <Route index element={<CustomerDashboard />} />
          <Route path="account" element={<Account />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="organization" element={<CustomerOrgDetails />} />
          <Route
            path="organization/users"
            element={
              <RequireCustomerRole anyOf={[ROLE_IT]} title="Users">
                <CustomerUsers />
              </RequireCustomerRole>
            }
          />
          <Route
            path="organization/notifications"
            element={<CustomerOrgNotifications />}
          />
          <Route
            path="organization/software"
            element={
              <RequireCustomerRole
                anyOf={[ROLE_OPERATIONS, ROLE_IT]}
                title="Software Downloads"
              >
                <CustomerSoftwareDownloads />
              </RequireCustomerRole>
            }
          />
          <Route
            path="organization/users/:username"
            element={
              <RequireCustomerRole anyOf={[ROLE_IT]} title="User profile">
                <CustomerUserProfile />
              </RequireCustomerRole>
            }
          />
          <Route path="profile" element={<Navigate to="/organization" replace />} />
          <Route
            path="profile/users/:username"
            element={<RedirectLegacyProfileUser />}
          />
          <Route
            path="slides/smart-agent"
            element={<Navigate to="/organization/software" replace />}
          />
          <Route
            path="reports"
            element={
              <RequireCustomerRole anyOf={[ROLE_BILLING]} title="Reports">
                <CustomerReports />
              </RequireCustomerRole>
            }
          />
          <Route
            path="slides/upload"
            element={
              <RequireCustomerRole anyOf={[ROLE_OPERATIONS]} title="Upload">
                <CustomerSlideUpload />
              </RequireCustomerRole>
            }
          />
          <Route
            path="slides/upload-activity"
            element={
              <RequireCustomerRole
                anyOf={[ROLE_OPERATIONS]}
                title="Upload Activity"
              >
                <CustomerUploadActivity />
              </RequireCustomerRole>
            }
          />
          <Route
            path="slides/:jobId"
            element={
              <RequireCustomerRole
                anyOf={[ROLE_VIEWER, ROLE_OPERATIONS]}
                title="Slide details"
              >
                <CustomerSlideDetail />
              </RequireCustomerRole>
            }
          />
          <Route
            path="slides"
            element={
              <RequireCustomerRole anyOf={[ROLE_VIEWER]} title="Slide Lookup">
                <CustomerSlideLookup />
              </RequireCustomerRole>
            }
          />
        </Route>
      </Route>
    </Routes>
  );
}

function ImpersonationApp() {
  const signOut = useMemo(
    () => () => {
      clearImpersonationSession();
      // Tab was opened via window.open from admin; browsers allow close in that case.
      window.close();
      // Fallback if the browser refuses to close (not script-opened).
      window.location.replace("about:blank");
    },
    []
  );

  return (
    <BrowserRouter>
      <PortalRoutes
        user={{ username: "impersonation" }}
        signOut={signOut}
      />
    </BrowserRouter>
  );
}

function isImpersonationEntry() {
  if (typeof window === "undefined") return false;
  if (hasImpersonationSession()) return true;
  return window.location.pathname.replace(/\/$/, "") === "/impersonate";
}

export default function App() {
  // Latch impersonation shell for this page load (bootstrap + session).
  const [impersonationShell] = useState(() => isImpersonationEntry());

  if (impersonationShell) {
    return <ImpersonationApp />;
  }

  return (
    <Authenticator>
      {({ user, signOut }) => (
        <BrowserRouter>
          <PortalRoutes user={user} signOut={signOut} />
        </BrowserRouter>
      )}
    </Authenticator>
  );
}
